'use client'

import { useState } from 'react'
import Link from 'next/link'
import { Shield, TrendingUp, Globe, CheckCircle, ArrowRight, Zap, FileCheck, Users, Lock } from 'lucide-react'

export default function DemoHomePage() {
  const [activeStep, setActiveStep] = useState(1)

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Premium & Professional */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
        
        <div className="relative container mx-auto px-4 py-32 md:py-40">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-400/20 backdrop-blur-sm mb-8">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-blue-300 text-sm font-medium">GDPR • NIS2 • DORA Compliance Platform</span>
              </div>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight text-center">
              Achieve Digital Sovereignty
              <br />
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Without the Complexity
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 mb-12 max-w-3xl mx-auto text-center leading-relaxed">
              Automated compliance scanning, risk assessment, and EU alternatives discovery for GDPR, NIS2, and DORA regulations.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link
                href="/register"
                className="group px-8 py-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all text-lg shadow-lg shadow-blue-600/50 hover:shadow-xl hover:shadow-blue-600/60 flex items-center justify-center gap-2"
              >
                Start Free Assessment
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                href="/alternatives"
                className="px-8 py-4 text-white rounded-xl font-semibold border-2 border-white/20 hover:bg-white/10 backdrop-blur-sm transition-all text-lg flex items-center justify-center gap-2"
              >
                <Globe className="w-5 h-5" />
                EU Alternatives
              </Link>
            </div>

            <div className="flex flex-wrap justify-center gap-8 text-slate-400 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                No credit card required
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                5-minute setup
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                100% EU data hosting
              </div>
            </div>
          </div>
        </div>

        {/* Wave separator */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" fill="white"/>
          </svg>
        </div>
      </div>

      {/* Trust Indicators - Industries */}
      <div className="relative bg-gradient-to-b from-slate-50 to-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-4">
                <span className="text-blue-600 text-sm font-semibold">Trusted Across Industries</span>
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
                Compliance Made Simple
              </h3>
              <p className="text-slate-600 text-lg">
                Built for regulated sectors that can't afford compliance gaps
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {/* Financial Services */}
              <div className="group relative bg-white rounded-2xl p-8 border-2 border-slate-100 hover:border-blue-400 hover:shadow-2xl transition-all cursor-default overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/5 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500" />
                <div className="relative">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg shadow-blue-500/30">
                    <span className="text-3xl">🏦</span>
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2 text-lg">Financial Services</h4>
                  <p className="text-sm text-slate-500 leading-relaxed">DORA compliance mandatory for banks & fintech</p>
                </div>
              </div>

              {/* Healthcare */}
              <div className="group relative bg-white rounded-2xl p-8 border-2 border-slate-100 hover:border-green-400 hover:shadow-2xl transition-all cursor-default overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-green-500/5 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500" />
                <div className="relative">
                  <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg shadow-green-500/30">
                    <span className="text-3xl">🏥</span>
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2 text-lg">Healthcare</h4>
                  <p className="text-sm text-slate-500 leading-relaxed">Patient data protection & medical privacy</p>
                </div>
              </div>

              {/* SaaS & Tech */}
              <div className="group relative bg-white rounded-2xl p-8 border-2 border-slate-100 hover:border-purple-400 hover:shadow-2xl transition-all cursor-default overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-purple-500/5 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500" />
                <div className="relative">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg shadow-purple-500/30">
                    <span className="text-3xl">💻</span>
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2 text-lg">SaaS & Tech</h4>
                  <p className="text-sm text-slate-500 leading-relaxed">NIS2 infrastructure & cloud security</p>
                </div>
              </div>

              {/* E-commerce */}
              <div className="group relative bg-white rounded-2xl p-8 border-2 border-slate-100 hover:border-orange-400 hover:shadow-2xl transition-all cursor-default overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-orange-500/5 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500" />
                <div className="relative">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg shadow-orange-500/30">
                    <span className="text-3xl">🛒</span>
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2 text-lg">E-commerce</h4>
                  <p className="text-sm text-slate-500 leading-relaxed">Customer data GDPR & payment security</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Value Proposition - Cards */}
      <div className="bg-slate-50 py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              The Cost of Non-Compliance is Too High
            </h2>
            <p className="text-xl text-slate-600">
              European regulations aren't suggestions — they're requirements with severe penalties
            </p>
          </div>

          <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <span className="text-3xl">💰</span>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">€20M in Fines</h3>
              <p className="text-slate-600 mb-4">
                GDPR penalties reach €20M or 4% of global revenue. NIS2 fines up to €10M. DORA mandatory for financial institutions.
              </p>
              <div className="text-sm text-red-600 font-semibold">
                427 GDPR fines issued in 2025
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                <span className="text-3xl">⏱️</span>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">300+ Hours Lost</h3>
              <p className="text-slate-600 mb-4">
                Manual compliance audits consume months of engineering time. Spreadsheets become outdated the moment they're created.
              </p>
              <div className="text-sm text-orange-600 font-semibold">
                Average: 6 weeks per audit cycle
              </div>
            </div>

            {/* Card 3 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center mb-6">
                <span className="text-3xl">🎯</span>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Vendor Lock-In</h3>
              <p className="text-slate-600 mb-4">
                US cloud giants control pricing and data access. EU alternatives offer transparency, competitive pricing, and local support.
              </p>
              <div className="text-sm text-yellow-600 font-semibold">
                30% avg. cost savings with EU providers
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works - Enhanced Timeline */}
      <div className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-24">
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-grid-white/[0.05]" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Compliance in Three Simple Steps
            </h2>
            <p className="text-xl text-slate-300">
              From zero to full visibility in under 10 minutes
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            {/* Tab Navigation */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12 justify-center">
              <button
                onClick={() => setActiveStep(1)}
                className={`group relative px-8 py-4 rounded-xl font-semibold text-lg transition-all ${
                  activeStep === 1
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-xl shadow-blue-600/30'
                    : 'bg-white/10 text-slate-300 border-2 border-white/20 hover:border-blue-400 hover:text-white hover:bg-white/15'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    activeStep === 1 ? 'bg-white/20' : 'bg-white/10'
                  }`}>
                    <span className={`font-bold ${activeStep === 1 ? 'text-white' : 'text-slate-300'}`}>1</span>
                  </div>
                  <span>Connect</span>
                </div>
              </button>

              <button
                onClick={() => setActiveStep(2)}
                className={`group relative px-8 py-4 rounded-xl font-semibold text-lg transition-all ${
                  activeStep === 2
                    ? 'bg-gradient-to-r from-cyan-600 to-cyan-700 text-white shadow-xl shadow-cyan-600/30'
                    : 'bg-white/10 text-slate-300 border-2 border-white/20 hover:border-cyan-400 hover:text-white hover:bg-white/15'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    activeStep === 2 ? 'bg-white/20' : 'bg-white/10'
                  }`}>
                    <span className={`font-bold ${activeStep === 2 ? 'text-white' : 'text-slate-300'}`}>2</span>
                  </div>
                  <span>Analyze</span>
                </div>
              </button>

              <button
                onClick={() => setActiveStep(3)}
                className={`group relative px-8 py-4 rounded-xl font-semibold text-lg transition-all ${
                  activeStep === 3
                    ? 'bg-gradient-to-r from-green-600 to-green-700 text-white shadow-xl shadow-green-600/30'
                    : 'bg-white/10 text-slate-300 border-2 border-white/20 hover:border-green-400 hover:text-white hover:bg-white/15'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    activeStep === 3 ? 'bg-white/20' : 'bg-white/10'
                  }`}>
                    <span className={`font-bold ${activeStep === 3 ? 'text-white' : 'text-slate-300'}`}>3</span>
                  </div>
                  <span>Act</span>
                </div>
              </button>
            </div>

            {/* Tab Content */}
            <div className="relative bg-white/5 rounded-3xl p-12 shadow-2xl border border-white/10">
              {/* Grid pattern overlay */}
              <div className="absolute inset-0 bg-grid-white/[0.02] rounded-3xl" />
              
              {/* Step 1 Content */}
              {activeStep === 1 && (
                <div className="relative z-10 transition-all duration-500 ease-out">
                <div className="flex flex-col lg:flex-row gap-12 items-center">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-32 h-32 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-3xl flex items-center justify-center border border-blue-400/30">
                      <svg className="w-16 h-16 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="inline-block px-4 py-1.5 bg-blue-500/20 text-blue-300 rounded-full text-sm font-semibold mb-4 uppercase tracking-wide border border-blue-400/30">
                      Step 1: Connect
                    </div>
                    <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                      Import Your Tech Stack
                    </h3>
                    <p className="text-slate-300 text-lg mb-8 leading-relaxed">
                      Connect your infrastructure in seconds. Upload a CSV of your services, paste a list, or manually add assets. We automatically detect cloud providers, SaaS tools, APIs, databases, and third-party integrations.
                    </p>
                    
                    <div className="grid sm:grid-cols-2 gap-4 mb-8">
                      <div className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                        <CheckCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-white mb-1">CSV Bulk Upload</div>
                          <div className="text-sm text-slate-400">Import hundreds of assets at once</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                        <CheckCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-white mb-1">Auto-Detection</div>
                          <div className="text-sm text-slate-400">Smart dependency mapping</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                        <CheckCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-white mb-1">Manual Entry</div>
                          <div className="text-sm text-slate-400">Add assets one by one</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                        <CheckCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-white mb-1">Categorization</div>
                          <div className="text-sm text-slate-400">Cloud, SaaS, API classification</div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-500/10 border border-blue-400/30 rounded-xl p-6">
                      <p className="text-sm text-blue-200">
                        <strong>💡 Pro tip:</strong> Start with your critical infrastructure first, then expand to all services for complete visibility.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2 Content */}
            {activeStep === 2 && (
              <div className="relative z-10 transition-all duration-500 ease-out">
                <div className="flex flex-col lg:flex-row gap-12 items-center">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-32 h-32 bg-gradient-to-br from-cyan-500/20 to-cyan-600/20 rounded-3xl flex items-center justify-center border border-cyan-400/30">
                      <svg className="w-16 h-16 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="inline-block px-4 py-1.5 bg-cyan-500/20 text-cyan-300 rounded-full text-sm font-semibold mb-4 uppercase tracking-wide border border-cyan-400/30">
                      Step 2: Analyze
                    </div>
                    <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                      Get Instant Risk Scores
                    </h3>
                    <p className="text-slate-300 text-lg mb-8 leading-relaxed">
                      Our compliance engine automatically analyzes each asset against GDPR, NIS2, and DORA requirements. See real-time risk scores based on data processing location, vendor jurisdiction, and criticality level.
                    </p>
                    
                    <div className="grid sm:grid-cols-2 gap-4 mb-8">
                      <div className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                        <CheckCircle className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-white mb-1">GDPR Compliance</div>
                          <div className="text-sm text-slate-400">Data sovereignty checks</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                        <CheckCircle className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-white mb-1">NIS2 Assessment</div>
                          <div className="text-sm text-slate-400">Security & infrastructure risks</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                        <CheckCircle className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-white mb-1">DORA Scoring</div>
                          <div className="text-sm text-slate-400">Resilience & concentration</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                        <CheckCircle className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-white mb-1">Risk Prioritization</div>
                          <div className="text-sm text-slate-400">High to low severity ranking</div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-cyan-500/10 border border-cyan-400/30 rounded-xl p-6">
                      <p className="text-sm text-cyan-200">
                        <strong>🎯 Insight:</strong> High-risk assets are flagged instantly with specific regulation violations and remediation steps.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3 Content */}
            {activeStep === 3 && (
              <div className="relative z-10 transition-all duration-500 ease-out">
                <div className="flex flex-col lg:flex-row gap-12 items-center">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-32 h-32 bg-gradient-to-br from-green-500/20 to-green-600/20 rounded-3xl flex items-center justify-center border border-green-400/30">
                      <svg className="w-16 h-16 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="inline-block px-4 py-1.5 bg-green-500/20 text-green-300 rounded-full text-sm font-semibold mb-4 uppercase tracking-wide border border-green-400/30">
                      Step 3: Act
                    </div>
                    <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                      Take Action with Confidence
                    </h3>
                    <p className="text-slate-300 text-lg mb-8 leading-relaxed">
                      Download audit-ready compliance reports, discover EU alternatives for risky vendors, and get step-by-step migration roadmaps with cost-benefit analysis. Everything you need to make informed decisions.
                    </p>
                    
                    <div className="grid sm:grid-cols-2 gap-4 mb-8">
                      <div className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                        <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-white mb-1">PDF/JSON Exports</div>
                          <div className="text-sm text-slate-400">Audit-ready documentation</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                        <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-white mb-1">EU Alternatives</div>
                          <div className="text-sm text-slate-400">24+ vetted providers catalog</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                        <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-white mb-1">Migration Roadmaps</div>
                          <div className="text-sm text-slate-400">Step-by-step transition plans</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                        <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-white mb-1">ROI Calculations</div>
                          <div className="text-sm text-slate-400">Cost-benefit analysis</div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-500/10 border border-green-400/30 rounded-xl p-6">
                      <p className="text-sm text-green-200">
                        <strong>✅ Outcome:</strong> Receive actionable recommendations ranked by impact, effort, and cost savings potential.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Features Grid - Premium */}
      <div className="bg-gradient-to-b from-slate-50 to-white py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Everything You Need for Compliance
            </h2>
            <p className="text-xl text-slate-600">
              Comprehensive tools built specifically for European regulations
            </p>
          </div>

          <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Multi-Framework Compliance</h3>
              <p className="text-slate-600 text-sm">
                GDPR, NIS2, DORA coverage in one platform. Automatic cross-regulation correlation.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Globe className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">EU Alternatives Directory</h3>
              <p className="text-slate-600 text-sm">
                24+ vetted EU providers across cloud, SaaS, and infrastructure categories.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <FileCheck className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Audit-Ready Reports</h3>
              <p className="text-slate-600 text-sm">
                Generate compliance documentation in PDF/JSON for regulators and auditors.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Risk Trending</h3>
              <p className="text-slate-600 text-sm">
                Track compliance improvements over time. Visualize sovereignty score evolution.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-cyan-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Team Collaboration</h3>
              <p className="text-slate-600 text-sm">
                Multi-user workspaces with role-based access control for compliance teams.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">100% EU Hosting</h3>
              <p className="text-slate-600 text-sm">
                Your data never leaves the EU. Frankfurt & Paris data centers with full encryption.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Compliance & Standards Section */}
      <div className="bg-gradient-to-b from-white to-slate-50 py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <div className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4 uppercase tracking-wide">
              Compliance Coverage
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Navigate Every Major Regulation
            </h2>
            <p className="text-xl text-slate-600">
              Dependra helps you stay compliant with EU regulations and international standards
            </p>
          </div>

          {/* EU Regulations */}
          <div className="max-w-6xl mx-auto mb-12">
            <h3 className="text-lg font-semibold text-slate-700 mb-6 text-center">🇪🇺 EU Regulations</h3>
            <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
              <Link href="/compliance/gdpr" className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-blue-400 hover:shadow-lg transition-all text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 mx-auto group-hover:bg-blue-200 transition-colors">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="font-bold text-slate-900 mb-1">GDPR</h4>
                <p className="text-xs text-slate-500">Data Protection</p>
              </Link>

              <Link href="/compliance/nis2" className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-purple-400 hover:shadow-lg transition-all text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 mx-auto group-hover:bg-purple-200 transition-colors">
                  <Shield className="w-6 h-6 text-purple-600" />
                </div>
                <h4 className="font-bold text-slate-900 mb-1">NIS2</h4>
                <p className="text-xs text-slate-500">Cybersecurity</p>
              </Link>

              <Link href="/compliance/dora" className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-orange-400 hover:shadow-lg transition-all text-center">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4 mx-auto group-hover:bg-orange-200 transition-colors">
                  <Shield className="w-6 h-6 text-orange-600" />
                </div>
                <h4 className="font-bold text-slate-900 mb-1">DORA</h4>
                <p className="text-xs text-slate-500">Financial Resilience</p>
              </Link>

              <Link href="/compliance/eprivacy" className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-teal-400 hover:shadow-lg transition-all text-center">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4 mx-auto group-hover:bg-teal-200 transition-colors">
                  <Shield className="w-6 h-6 text-teal-600" />
                </div>
                <h4 className="font-bold text-slate-900 mb-1">ePrivacy</h4>
                <p className="text-xs text-slate-500">Electronic Privacy</p>
              </Link>

              <Link href="/compliance/ai-act" className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-violet-400 hover:shadow-lg transition-all text-center">
                <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center mb-4 mx-auto group-hover:bg-violet-200 transition-colors">
                  <Shield className="w-6 h-6 text-violet-600" />
                </div>
                <h4 className="font-bold text-slate-900 mb-1">AI Act</h4>
                <p className="text-xs text-slate-500">AI Governance</p>
              </Link>
            </div>
          </div>

          {/* International Standards */}
          <div className="max-w-4xl mx-auto">
            <h3 className="text-lg font-semibold text-slate-700 mb-6 text-center">🌍 International Standards</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link href="/compliance/iso-27001" className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-indigo-400 hover:shadow-lg transition-all text-center">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4 mx-auto group-hover:bg-indigo-200 transition-colors">
                  <Shield className="w-6 h-6 text-indigo-600" />
                </div>
                <h4 className="font-bold text-slate-900 mb-1">ISO 27001</h4>
                <p className="text-xs text-slate-500">Information Security</p>
              </Link>

              <Link href="/compliance/soc2" className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-red-400 hover:shadow-lg transition-all text-center">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4 mx-auto group-hover:bg-red-200 transition-colors">
                  <Shield className="w-6 h-6 text-red-600" />
                </div>
                <h4 className="font-bold text-slate-900 mb-1">SOC 2</h4>
                <p className="text-xs text-slate-500">Trust Services</p>
              </Link>

              <Link href="/compliance/hipaa" className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all text-center">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4 mx-auto group-hover:bg-emerald-200 transition-colors">
                  <Shield className="w-6 h-6 text-emerald-600" />
                </div>
                <h4 className="font-bold text-slate-900 mb-1">HIPAA</h4>
                <p className="text-xs text-slate-500">Healthcare Data</p>
              </Link>

              <Link href="/compliance/pci-dss" className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-green-400 hover:shadow-lg transition-all text-center">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4 mx-auto group-hover:bg-green-200 transition-colors">
                  <Shield className="w-6 h-6 text-green-600" />
                </div>
                <h4 className="font-bold text-slate-900 mb-1">PCI DSS</h4>
                <p className="text-xs text-slate-500">Payment Security</p>
              </Link>
            </div>
          </div>

          {/* Security Link */}
          <div className="max-w-xl mx-auto mt-12 text-center">
            <Link href="/security" className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors">
              <Lock className="w-5 h-5" />
              View Our Security Practices
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>


      {/* Stats Bar */}
      <div className="bg-slate-900 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-white mb-2">3</div>
              <div className="text-slate-400">Compliance Frameworks</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">24+</div>
              <div className="text-slate-400">EU Alternatives</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">€20M</div>
              <div className="text-slate-400">Max GDPR Fine</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">100%</div>
              <div className="text-slate-400">EU Data Hosting</div>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA - Premium */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-600 py-24">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" />
        
        <div className="relative container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Start Your Compliance Journey Today
          </h2>
          <p className="text-xl text-blue-100 mb-12 max-w-2xl mx-auto">
            Achieve digital sovereignty and regulatory compliance without complexity
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link
              href="/register"
              className="group px-10 py-5 bg-white text-blue-600 rounded-xl font-bold hover:bg-blue-50 transition-all text-lg shadow-2xl hover:scale-105 flex items-center justify-center gap-2"
            >
              Start Free Assessment
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              href="/pricing"
              className="px-10 py-5 text-white rounded-xl font-bold border-2 border-white/30 hover:bg-white/10 backdrop-blur-sm transition-all text-lg flex items-center justify-center gap-2"
            >
              View Pricing
            </Link>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-blue-100 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              14-day free trial
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              No credit card required
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Cancel anytime
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}
